import { type ComponentProps, useRef, type ReactNode } from 'react';
import * as styles from './styles.css';
import {
  type UseComboboxProps,
  useCombobox,
  useMultipleSelection,
} from 'downshift';
import { Bleed, Box, ButtonIcon, IconClear, Text } from 'braid-design-system';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';

interface BaseDownshiftItem {
  id: string;
  name: string;
}

interface MultiSelectAutosuggestProps<T extends BaseDownshiftItem> {
  id: string;
  label: string;
  suggestions: T[];
  clearLabel?: string;
  placeholder?: string;
  icon?: JSX.Element;
  selectedItems: T[];
  searchText: string;
  hideLabel?: boolean;
  dropdownBoxProps?: ComponentProps<typeof Box>;
  setSearchText: (value: string) => void;
  onItemsSelected: (items: T[]) => void;
  renderSuggestions: (props: {
    highlightedIndex: ReturnType<typeof useCombobox<T>>['highlightedIndex'];
    getItemProps: ReturnType<typeof useCombobox<T>>['getItemProps'];
  }) => JSX.Element;
  renderSelectedItems?: (props: {
    getSelectedItemProps: ReturnType<
      typeof useMultipleSelection<T>
    >['getSelectedItemProps'];
  }) => JSX.Element;
  onClickSuggestionItem: (item: T) => void;
  onMenuClose?: () => void;
  isItemDisabled?: UseComboboxProps<T>['isItemDisabled'];
}

export const MultiSelectAutosuggest = <T extends BaseDownshiftItem>({
  id,
  label,
  suggestions,
  selectedItems,
  clearLabel,
  icon,
  placeholder,
  searchText,
  hideLabel = false,
  dropdownBoxProps = {},
  setSearchText,
  onClickSuggestionItem,
  onItemsSelected,
  renderSelectedItems,
  renderSuggestions,
  onMenuClose,
  isItemDisabled,
}: MultiSelectAutosuggestProps<T>): ReactNode => {
  const { t } = useTranslations(translations);
  const inputRef = useRef<HTMLInputElement>(null);

  const { getSelectedItemProps, getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ type, selectedItems: newSelectedItems }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onItemsSelected(newSelectedItems ?? []);
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu,
  } = useCombobox({
    items: suggestions,
    itemToString: (item) => (item ? item.id : ''),
    inputValue: searchText,
    isItemDisabled: isItemDisabled ?? (() => false),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keeps the menu open after selection.
            highlightedIndex: state.highlightedIndex,
          };
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          if (onMenuClose) {
            onMenuClose();
          }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          setSearchText(newInputValue ?? '');
          break;
        default:
          break;
      }
    },
  });

  const hasSelectedItems = selectedItems.length > 0;
  const shouldShowClearIcon = searchText.length > 0;

  return (
    <Box width="full" position="relative" className={styles.componentContainer}>
      <Box
        display={hideLabel ? 'none' : 'block'}
        component="label"
        {...getLabelProps({
          onClick: () => {
            openMenu();
          },
        })}
      >
        <Text size="small" weight="strong">
          {label}
        </Text>
      </Box>
      <Box
        position="relative"
        width="full"
        onClick={(e) => {
          openMenu();
          e.stopPropagation();
        }}
        paddingY={hasSelectedItems ? 'xsmall' : 'small'}
        className={{
          [styles.inputParent]: true,
          [styles.noClearIconSpace]: shouldShowClearIcon,
        }}
      >
        {icon ? (
          <Text
            size="small"
            baseline={false}
            tone={hasSelectedItems || searchText ? 'neutral' : 'secondary'}
          >
            {icon}
          </Text>
        ) : null}

        {renderSelectedItems
          ? renderSelectedItems({ getSelectedItemProps })
          : null}

        <input
          className={styles.input}
          placeholder={hasSelectedItems ? '' : placeholder}
          {...getInputProps(getDropdownProps({ ref: inputRef }), {
            suppressRefError: true,
          })}
        />
        {shouldShowClearIcon ? (
          <Box
            component="span"
            position="absolute"
            width="touchable"
            height="touchable"
            display="flex"
            alignItems="center"
            justifyContent="center"
            top={0}
            right={0}
          >
            <Bleed vertical="xxsmall">
              <ButtonIcon
                id={`${id}-clear-multiselect-autosuggest-input`}
                label={clearLabel || t('Clear')}
                variant="transparent"
                tone="neutral"
                type="reset"
                onClick={() => setSearchText('')}
                icon={<IconClear />}
              />
            </Bleed>
          </Box>
        ) : null}
      </Box>
      <Box
        component="ul"
        display={isOpen ? 'block' : 'none'}
        {...dropdownBoxProps}
        className={styles.suggestionList}
        {...getMenuProps({}, { suppressRefError: true })}
      >
        {renderSuggestions({ highlightedIndex, getItemProps })}
      </Box>
    </Box>
  );
};
